import { Avatar, Divider, Grid, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ArrowDown, ArrowUp, Equal } from "lucide-react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { CardRankingRowProps } from "./CardRanking.types";
import { StyledUserItem } from "./CardRankingRow.styles";
import { TREND } from "../../consts";
import { useLeaderboardClusterStore } from "../../stores";
import { Icon } from "../Icon";

export function CardRankingRow({ observer, observeStudent, student }: CardRankingRowProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const leaderboardConfiguration = useLeaderboardClusterStore(state => state.currentCluster);
  const selfRef = useRef<HTMLDivElement | null>(null);

  const abbreviatedName = student?.studentName
    ?.split(" ")
    ?.slice(0, 2)
    ?.map((slice) => slice[0]);

  useEffect(() => {
    observeStudent && selfRef.current && observer?.observe(selfRef.current);
  }, [observer, observeStudent]);

  const TrendIcon = (student?.trend === TREND.EQUALS) ? Equal : (student?.trend === TREND.UP) ? ArrowUp : ArrowDown;
  const trendIconColor = (student?.trend === TREND.EQUALS) ? theme.customColors.systemPrimary01 : (student?.trend === TREND.UP) ? theme.customColors.systemSecondary01 : theme.customColors.textWarning;

  return (
    <StyledUserItem
      role={ student.isCaller ? "listitem" : undefined }
      tabIndex={ 0 }
      aria-labelledby={ `sr-label-${student.studentEid}` }
      key={ `ranking-item--${student?.studentEid}` }
      container
      ref={ selfRef }
      $isActive={ student?.isCaller }
      sx={ { color:theme.customColors.exploreCardText } }
    >
      <Grid
        id={ `sr-label-${student.studentEid}` }
        aria-label={ student.isCaller
          ? `${t("your_score")} ${student?.totalScore} ${t("score_text")}`
          : `${t("classified")} ${student?.ranking} ${t("score")} ${student?.totalScore}` }
        item
        xs={ 3 }
      >
        <Stack direction={ "row" } alignItems={ "center" } justifyContent={ "start" } gap={ "4px" }>
          <TrendIcon
            color={ trendIconColor }
            size={ 14 }
          />
          <Typography whiteSpace={ "nowrap" }>{ student?.ranking ?? "--" }</Typography>
        </Stack>
      </Grid>
      <Grid
        aria-hidden={ "true" }
        item
        xs={ 6 }
        alignItems={ "center" }
        display={ "flex" }
        gap={ theme.spacing(0.5) }
        justifyContent={ "start" }
        maxWidth={ "50%" }
        width={ "100%" }
      >
        {
          (leaderboardConfiguration?.anonymize === false || student?.isCaller) ? (
            <Avatar
              alt={ student?.studentName }
              // sizes=theme.spacing(3)
              src={ student?.profilePic }
              sx={ {
                fontSize: theme.spacing(1.5),
                height: "30px",
                width: "30px"
              } }
            >
              { abbreviatedName }
            </Avatar>
          ) : (
            <Icon
              icon="Icons_Profilo"
              color={ theme.customColors.exploreCardText }
              size={ 30 }
            />
          )
        }
        <Divider
          aria-hidden="true"
          orientation="vertical"
          sx={ {
            color: "transparent",
            width: "0.3rem"
          } }
        />
        <Typography
          sx={ {
            maxWidth: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          } }
          fontSize={ 12 }
          fontWeight={ 500 }
        >
          { student?.studentName }
        </Typography>
      </Grid>
      <Grid
        aria-hidden="true"
        item
        xs={ 3 }
      >
        <Typography fontSize={ 12 } fontWeight={ 500 } textAlign={ "right" } whiteSpace={ "nowrap" }>
          { student?.totalScore + ` ${t("score_text")}`  }
        </Typography>
      </Grid>
    </StyledUserItem>
  );
}
